import React, {useEffect, useReducer, useState} from 'react'
import DashboardHeader from "../layouts/dashboard_header";
import SideMenu from "../layouts/dashboard_menu";
import Canvas from "../layouts/dahboard_canvas";
import DashboardFooter from "../layouts/dashboard_footer";
import {useNavigate} from "react-router-dom";
import {useUserNames} from "../../utils/utils";
import Swal from "sweetalert2";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faMicrochip, 
    faWifi, 
    faSignal, 
    faBatteryThreeQuarters,
    faChartLine,
    faExclamationTriangle,
    faCheckCircle,
    faTimesCircle,
    faPlus,
    faEdit,
    faTrash,
    faSync,
    faDownload,
    faUpload,
    faCog,
    faEye,
    faHistory,
    faThermometerHalf,
    faHeartbeat,
    faTint,
    faWeightHanging,
    faBed,
    faRunning,
    faNetworkWired,
    faMicrochip as faChip,
    faStethoscope,
    faXRay,
    faSyringe,
    faSatelliteDish,
    faUserMd,
    faHospital,
    faShieldAlt,
    faRocket
} from '@fortawesome/free-solid-svg-icons';
import { 
    DeviceReading, 
    DeviceReadingResult, 
    DeviceConnection, 
    createUSBConnection, 
    createBluetoothConnection, 
    createWiFiConnection, 
    createUARTConnection 
} from "../../utils/deviceConnections";
import BluetoothDeviceManager from "../../components/BluetoothDeviceManager";
import { BluetoothDeviceInfo, BluetoothReading } from "../../utils/bluetoothDeviceManager";

// Connection types
enum ConnectionType {
    USB = 'USB',
    Bluetooth = 'Bluetooth',
    WiFi = 'WiFi',
    UART = 'UART/USART'
}

const Devices: React.FC = () => {
    // Add custom styles for animations
    useEffect(() => {
        const style = document.createElement('style');
        style.textContent = `
            @keyframes pulse {
                0% { transform: scale(1); }
                50% { transform: scale(1.05); }
                100% { transform: scale(1); }
            }
            @keyframes fadeInUp {
                from { opacity: 0; transform: translateY(20px); }
                to { opacity: 1; transform: translateY(0); }
            }
            .card-hover:hover {
                box-shadow: 0 10px 25px rgba(0,0,0,0.15) !important;
            }
            .bg-gradient-primary { background: #007bff; }
            .bg-gradient-success { background: #28a745; }
            .bg-gradient-info { background: #17a2b8; }
            .bg-gradient-warning { background: #ffc107; }
            
            /* Bluetooth Device Manager Styles */
            .bluetooth-device-manager {
                animation: fadeInUp 0.5s ease-out;
            }
            .device-card {
                transition: all 0.3s ease;
                border: 1px solid #e9ecef;
            }
            .device-card:hover {
                transform: translateY(-2px);
                box-shadow: 0 8px 25px rgba(0,0,0,0.1);
            }
            .device-list {
                max-height: 500px;
                overflow-y: auto;
            }
            .fa-spin {
                animation: fa-spin 1s infinite linear;
            }
            
            /* Modal Styles */
            .modal.show {
                z-index: 1050;
            }
            .modal-dialog.modal-xl {
                max-width: 90%;
            }
            .bluetooth-device-manager .device-list {
                max-height: 400px;
                overflow-y: auto;
            }
        `;
        document.head.appendChild(style);
        
        return () => {
            document.head.removeChild(style);
        };
    }, []);

    const [step, setStep] = useState(2); // default to device selection
    const [readings, setReadings] = useState<any[]>([]);
    const [showExportOptions, setShowExportOptions] = useState(false);
    const [selectedConnectionType, setSelectedConnectionType] = useState<ConnectionType>(ConnectionType.Bluetooth);
    const [isConnecting, setIsConnecting] = useState(false);
    const [connectedDevice, setConnectedDevice] = useState<string | null>(null);
    const [deviceStats, setDeviceStats] = useState({
        totalDevices: 0,
        connectedDevices: 0,
        activeReadings: 0,
        lastUpdate: new Date()
    });
    const [showBluetoothManager, setShowBluetoothManager] = useState(false);
    const [bluetoothDevices, setBluetoothDevices] = useState<BluetoothDeviceInfo[]>([]);
    const [bluetoothReadings, setBluetoothReadings] = useState<BluetoothReading[]>([]);

    // Handle modal backdrop click
    const handleModalBackdropClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            setShowBluetoothManager(false);
        }
    };

    const categories = [
        { name: 'Vitals', icon: faHeartbeat, color: 'primary', description: 'Heart rate, blood pressure, temperature, pulse oximeter' },
        { name: 'Diagnosis', icon: faStethoscope, color: 'success', description: 'ECG, ultrasound, diagnostic tools, stethoscopes' },
        { name: 'Imaging', icon: faXRay, color: 'info', description: 'X-ray, MRI, CT scan, ultrasound imaging' },
        { name: 'Point-Of-Care', icon: faSyringe, color: 'warning', description: 'Glucose monitors, rapid tests, blood analyzers' },
        { name: 'Wearables', icon: faRunning, color: 'secondary', description: 'Fitness trackers, smart watches, health monitors' },
        { name: 'Sensors', icon: faSatelliteDish, color: 'dark', description: 'Environmental sensors, monitoring devices' },
        { name: 'Respiratory', icon: faBed, color: 'info', description: 'Ventilators, spirometers, oxygen concentrators' },
        { name: 'Laboratory', icon: faMicrochip, color: 'purple', description: 'Lab analyzers, centrifuges, microscopes' },
        { name: 'Surgical', icon: faUserMd, color: 'danger', description: 'Surgical instruments, endoscopes, lasers' },
        { name: 'Rehabilitation', icon: faRunning, color: 'success', description: 'Physical therapy equipment, mobility aids' },
        { name: 'Emergency', icon: faExclamationTriangle, color: 'danger', description: 'Defibrillators, emergency monitors' },
        { name: 'Pharmacy', icon: faSyringe, color: 'warning', description: 'Medication dispensers, infusion pumps' },
        { name: 'My Devices', icon: faHospital, color: 'primary', description: 'Personal device collection and favorites' },
    ];

    // Connection handlers for different connection types using actual device implementations
    const usbConnection: DeviceConnection = createUSBConnection();
    const bluetoothConnection: DeviceConnection = createBluetoothConnection();
    const wifiConnection: DeviceConnection = createWiFiConnection();
    const uartConnection: DeviceConnection = createUARTConnection();

    // Get connection handler based on connection type
    const getConnectionHandler = (type: ConnectionType): DeviceConnection => {
        switch (type) {
            case ConnectionType.USB:
                return usbConnection;
            case ConnectionType.Bluetooth:
                return bluetoothConnection;
            case ConnectionType.WiFi:
                return wifiConnection;
            case ConnectionType.UART:
                return uartConnection;
            default:
                return bluetoothConnection;
        }
    };

    // Bluetooth manager handlers
    const handleBluetoothDeviceConnected = (device: BluetoothDeviceInfo) => {
        setBluetoothDevices(prev => {
            const existing = prev.find(d => d.id === device.id);
            if (existing) {
                return prev.map(d => d.id === device.id ? device : d);
            }
            return [...prev, device];
        });
        setConnectedDevice(device.name);
    };

    const handleBluetoothDataReceived = (reading: BluetoothReading) => {
        setBluetoothReadings(prev => [...prev, reading]);
        setReadings(prev => [...prev, {
            category: 'Bluetooth Device',
            connectionType: 'Bluetooth',
            timestamp: reading.timestamp,
            readings: reading.readings,
            deviceId: reading.deviceId
        }]);
    };

    const handleBluetoothError = (error: Error) => {
        console.error('Bluetooth error:', error);
        Swal.fire({
            icon: 'error',
            title: 'Bluetooth Error',
            text: error.message
        });
    };

    const handleCategoryClick = async (label: string) => {
        const { value: connectionTypeValue } = await Swal.fire({
            title: `Connect to ${label} device`,
            text: "Select connection method:",
            input: 'select',
            inputOptions: {
                [ConnectionType.USB]: 'USB',
                [ConnectionType.Bluetooth]: 'Bluetooth',
                [ConnectionType.WiFi]: 'WiFi',
                [ConnectionType.UART]: 'UART'
            },
            inputPlaceholder: 'Select a connection method',
            showCancelButton: true,
            confirmButtonText: "Connect",
            inputValidator: (value) => {
                return new Promise((resolve) => {
                    if (value) {
                        resolve('');
                    } else {
                        resolve('You need to select a connection method');
                    }
                });
            }
        });

        if (connectionTypeValue) {
            if (connectionTypeValue === ConnectionType.Bluetooth) {
                // Show Bluetooth device manager
                setShowBluetoothManager(true);
                setSelectedConnectionType(ConnectionType.Bluetooth);
                return;
            }

            try {
                setIsConnecting(true);
                setSelectedConnectionType(connectionTypeValue as ConnectionType);

                const connectionHandler = getConnectionHandler(connectionTypeValue as ConnectionType);

                // Show connecting message
                Swal.fire({
                    title: `Connecting to ${label} device via ${connectionTypeValue}...`,
                    text: 'Please wait while we establish connection',
                    allowOutsideClick: false,
                    didOpen: () => {
                        Swal.showLoading();
                    }
                });

                // Connect to device
                const result = await connectionHandler.connect(label);

                // Close loading alert
                Swal.close();

                // Set connected device
                setConnectedDevice(label);

                // Update readings
                setReadings(prev => [...prev, { 
                    category: label, 
                    connectionType: connectionTypeValue,
                    ...result 
                }]);

                setShowExportOptions(true);
                setStep(3);

                // Show success message
                await Swal.fire({
                    title: 'Connected!',
                    text: `Successfully connected to ${label} device via ${connectionTypeValue}`,
                    icon: 'success',
                    timer: 2000,
                    showConfirmButton: false
                });
            } catch (error) {
                console.error('Connection error:', error);
                await Swal.fire({
                    title: 'Connection Failed',
                    text: `Failed to connect to ${label} device: ${error}`,
                    icon: 'error'
                });
            } finally {
                setIsConnecting(false);
            }
        }
    };

    const handleDisconnect = async () => {
        if (connectedDevice && !isConnecting) {
            try {
                const connectionHandler = getConnectionHandler(selectedConnectionType);

                // Show disconnecting message
                Swal.fire({
                    title: `Disconnecting from ${connectedDevice}...`,
                    allowOutsideClick: false,
                    didOpen: () => {
                        Swal.showLoading();
                    }
                });

                // Disconnect from device
                await connectionHandler.disconnect();

                // Close loading alert
                Swal.close();

                // Clear connected device
                setConnectedDevice(null);

                // Show success message
                await Swal.fire({
                    title: 'Disconnected',
                    text: `Successfully disconnected from ${connectedDevice}`,
                    icon: 'info',
                    timer: 2000,
                    showConfirmButton: false
                });
            } catch (error) {
                console.error('Disconnection error:', error);
                await Swal.fire({
                    title: 'Disconnection Failed',
                    text: `Failed to disconnect from ${connectedDevice}: ${error}`,
                    icon: 'error'
                });
            }
        }
    };

    const exportPDF = () => {
        Swal.fire("Exported", "PDF has been generated successfully.", "success");
    };

    const shareToCollaboration = () => {
        Swal.fire("Shared", "Reading shared with existing collaboration.", "info");
    };

    const startNewCollaboration = () => {
        Swal.fire("Started", "New collaboration has been initiated.", "success");
    };

    const navigate = useNavigate()

    let user_info: any = {first_name: "", last_name: "", clinic: "", staff_id: "", avatar: ""}

    useEffect(()=>{
        document.title = "Uzima Device Center"
        const GetUsers = async () => {
            const user_info = useUserNames()
        }
        user_info = GetUsers()
        
        // Update device stats
        setDeviceStats({
            totalDevices: categories.length,
            connectedDevices: connectedDevice ? 1 : 0,
            activeReadings: readings.length,
            lastUpdate: new Date()
        });
    }, [connectedDevice, readings])

    const initialState = {
        step: 0,
        subStep: 0,
        cardStep: 0,
        header: "Uzima Devices Center",
        subHeader: "Manage all your devices from one place.",
        showHeadings: true,
        partnerOrPatient: "patient"
    }

    const reducer = (state: any, action: any) => {
        switch(action.type){
            case 'SET_STEP':
                return {...state, step: action.payload}
            case 'SET_PAGE_HEADING':
                return {...state, header: action.payload}
            case 'SET_SUB_HEADER':
                return {...state, subHeader: action.payload}
            case 'SET_SUB_STEP':
                return {...state, subStep: action.payload}
            case 'SET_CARD_STEP':
                return {...state, cardStep: action.payload}
            case 'SET_SHOW_HEADINGS':
                return {...state, showHeadings: action.payload}
            case 'SET_PARTNER_OR_PATIENT':
                return{...state, partnerOrPatient: action.payload}
            default:
                return state
        }
    }

    const [state, dispatch] = useReducer(reducer, initialState)

    const getConnectionIcon = (type: ConnectionType) => {
        switch (type) {
            case ConnectionType.USB: return faMicrochip;
            case ConnectionType.Bluetooth: return faSignal;
            case ConnectionType.WiFi: return faWifi;
            case ConnectionType.UART: return faNetworkWired;
            default: return faSignal;
        }
    };

    const getConnectionColor = (type: ConnectionType) => {
        switch (type) {
            case ConnectionType.USB: return 'text-warning';
            case ConnectionType.Bluetooth: return 'text-primary';
            case ConnectionType.WiFi: return 'text-success';
            case ConnectionType.UART: return 'text-info';
            default: return 'text-primary';
        }
    };

    return(
        <>
            <DashboardHeader />
            <SideMenu />
            <div className={"vertical-overlay"} />

            <div className={"main-content"}>
                <div className={"page-content"}>
                    <div className={"container-fluid"}>
                        
                        {/* Modern Header Section */}
                        {state.showHeadings && (
                            <div className="row mt-4 mb-5">
                                <div className="col-12 text-center">
                                    <div className="d-flex align-items-center justify-content-center mb-3">
                                        <div className="bg-primary bg-gradient rounded-circle p-3 me-3 shadow-lg" 
                                             style={{animation: 'pulse 2s infinite'}}>
                                            <FontAwesomeIcon icon={faRocket} className="text-white fa-2x" />
                                        </div>
                                        <h2 className="mb-0 text-primary fw-bold" 
                                            style={{textShadow: '0 2px 4px rgba(0,0,0,0.1)'}}>
                                            {state.header}
                                        </h2>
                                    </div>
                                    <p className="text-muted fs-5 mb-0">{state.subHeader}</p>
                                    <div className="mt-3">
                                        <div className="d-inline-block bg-success bg-opacity-10 px-3 py-2 rounded-pill">
                                            <FontAwesomeIcon icon={faShieldAlt} className="text-success me-2" />
                                            <small className="text-success fw-semibold">HIPAA Compliant • Secure • Real-time</small>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Device Statistics Dashboard */}
                        <div className="row g-4 mb-5">
                            <div className="col-md-3" style={{animation: 'fadeInUp 0.6s ease-out'}}>
                                <div className="card border-0 shadow-sm h-100 bg-primary text-white card-hover" 
                                     style={{transition: 'all 0.3s ease', cursor: 'pointer'}}
                                     onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-5px)'}
                                     onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}>
                                    <div className="card-body text-center p-4">
                                        <FontAwesomeIcon icon={faMicrochip} className="fa-3x mb-3 opacity-75" />
                                        <h3 className="fw-bold mb-1">{deviceStats.totalDevices}</h3>
                                        <p className="mb-0 opacity-90">Total Devices</p>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-3" style={{animation: 'fadeInUp 0.6s ease-out 0.1s both'}}>
                                <div className="card border-0 shadow-sm h-100 bg-success text-white card-hover"
                                     style={{transition: 'all 0.3s ease', cursor: 'pointer'}}
                                     onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-5px)'}
                                     onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}>
                                    <div className="card-body text-center p-4">
                                        <FontAwesomeIcon icon={faCheckCircle} className="fa-3x mb-3 opacity-75" />
                                        <h3 className="fw-bold mb-1">{deviceStats.connectedDevices}</h3>
                                        <p className="mb-0 opacity-90">Connected</p>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-3" style={{animation: 'fadeInUp 0.6s ease-out 0.2s both'}}>
                                <div className="card border-0 shadow-sm h-100 bg-info text-white card-hover"
                                     style={{transition: 'all 0.3s ease', cursor: 'pointer'}}
                                     onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-5px)'}
                                     onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}>
                                    <div className="card-body text-center p-4">
                                        <FontAwesomeIcon icon={faChartLine} className="fa-3x mb-3 opacity-75" />
                                        <h3 className="fw-bold mb-1">{deviceStats.activeReadings}</h3>
                                        <p className="mb-0 opacity-90">Active Readings</p>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-3" style={{animation: 'fadeInUp 0.6s ease-out 0.3s both'}}>
                                <div className="card border-0 shadow-sm h-100 bg-warning text-white card-hover"
                                     style={{transition: 'all 0.3s ease', cursor: 'pointer'}}
                                     onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-5px)'}
                                     onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}>
                                    <div className="card-body text-center p-4">
                                        <FontAwesomeIcon icon={faShieldAlt} className="fa-3x mb-3 opacity-75" />
                                        <h3 className="fw-bold mb-1">Secure</h3>
                                        <p className="mb-0 opacity-90">HIPAA Compliant</p>
                                    </div>
                                </div>
                            </div>
                        </div>



                        {step === 2 && (
                            <div className="row g-4">
                                {categories.map((category, index) => (
                                    <div className="col-md-6 col-lg-3" key={index}
                                         style={{animation: `fadeInUp 0.6s ease-out ${index * 0.1}s both`}}>
                                        <div className="card border-0 shadow-sm h-100 card-hover" 
                                             style={{ transition: 'all 0.3s ease', cursor: "pointer" }}
                                             onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-5px)'}
                                             onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                                             onClick={() => handleCategoryClick(category.name)}>
                                            <div className="card-body text-center p-4">
                                                <div className={`bg-${category.color} bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-3`} 
                                                     style={{width: '80px', height: '80px'}}>
                                                    <FontAwesomeIcon 
                                                        icon={category.icon} 
                                                        className={`text-${category.color} fa-2x`} 
                                                    />
                                                </div>
                                                <h5 className="card-title fw-bold mb-2">{category.name}</h5>
                                                <p className="card-text text-muted small mb-0">{category.description}</p>
                                                <div className="mt-3">
                                                    <span className="badge bg-primary">View Devices</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                
                                {/* Bluetooth Devices Card - Opens Modal */}
                                <div className="col-md-6 col-lg-3"
                                     style={{animation: `fadeInUp 0.6s ease-out ${categories.length * 0.1}s both`}}>
                                    <div className="card border-0 shadow-sm h-100 card-hover" 
                                         style={{ transition: 'all 0.3s ease', cursor: "pointer" }}
                                         onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-5px)'}
                                         onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                                         onClick={() => setShowBluetoothManager(true)}>
                                        <div className="card-body text-center p-4">
                                            <div className="bg-primary bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-3" 
                                                 style={{width: '80px', height: '80px'}}>
                                                <FontAwesomeIcon 
                                                    icon={faSignal} 
                                                    className="text-primary fa-2x" 
                                                />
                                            </div>
                                            <h5 className="card-title fw-bold mb-2">Bluetooth Devices</h5>
                                            <p className="card-text text-muted small mb-0">Connect to Bluetooth medical devices and sensors</p>
                                            <div className="mt-3">
                                                <span className="badge bg-primary">Open Bluetooth Manager</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Bluetooth Device Manager Modal */}
                        {showBluetoothManager && (
                            <div className="modal fade show d-block" 
                                 style={{backgroundColor: 'rgba(0,0,0,0.5)'}} 
                                 tabIndex={-1}
                                 onClick={handleModalBackdropClick}>
                                <div className="modal-dialog modal-xl modal-dialog-centered">
                                    <div className="modal-content border-0 shadow-lg">
                                        <div className="modal-header bg-primary text-white">
                                            <h5 className="modal-title">
                                                <FontAwesomeIcon icon={faSignal} className="me-2" />
                                                Bluetooth Device Manager
                                            </h5>
                                            <button 
                                                type="button" 
                                                className="btn-close btn-close-white" 
                                                onClick={() => setShowBluetoothManager(false)}
                                            ></button>
                                        </div>
                                        <div className="modal-body p-0">
                                            <BluetoothDeviceManager
                                                onDeviceConnected={handleBluetoothDeviceConnected}
                                                onDataReceived={handleBluetoothDataReceived}
                                                onError={handleBluetoothError}
                                            />
                                        </div>
                                        <div className="modal-footer">
                                            <button 
                                                type="button" 
                                                className="btn btn-secondary" 
                                                onClick={() => setShowBluetoothManager(false)}
                                            >
                                                <FontAwesomeIcon icon={faTimesCircle} className="me-2" />
                                                Close
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {step === 3 && (
                            <div className="mt-4">
                                {/* Enhanced Connection Status */}
                                {connectedDevice && (
                                    <div className="alert alert-info border-0 shadow-sm d-flex justify-content-between align-items-center mb-4">
                                        <div className="d-flex align-items-center">
                                            <FontAwesomeIcon icon={faCheckCircle} className="text-success me-3 fa-2x" />
                                            <div>
                                                <h6 className="mb-1 fw-bold">Device Connected Successfully!</h6>
                                                <p className="mb-0 text-muted">
                                                    <strong>{connectedDevice}</strong> via 
                                                    <span className={`badge bg-primary ms-2 ${getConnectionColor(selectedConnectionType)}`}>
                                                        <FontAwesomeIcon icon={getConnectionIcon(selectedConnectionType)} className="me-1" />
                                                        {selectedConnectionType}
                                                    </span>
                                                </p>
                                            </div>
                                        </div>
                                        <button 
                                            className="btn btn-outline-danger btn-lg" 
                                            onClick={handleDisconnect}
                                            disabled={isConnecting}
                                        >
                                            <FontAwesomeIcon icon={faTimesCircle} className="me-2" />
                                            Disconnect
                                        </button>
                                    </div>
                                )}

                                {/* Enhanced Readings Display */}
                                {readings.map((entry, idx) => (
                                    <div key={idx} className="mb-4 card border-0 shadow-sm">
                                        <div className="card-header bg-primary bg-opacity-10 border-0 d-flex justify-content-between align-items-center">
                                            <div className="d-flex align-items-center">
                                                <FontAwesomeIcon icon={faChartLine} className="text-primary me-2" />
                                                <h5 className="mb-0 fw-bold">{entry.category} - {entry.timestamp}</h5>
                                            </div>
                                            {entry.connectionType && (
                                                <span className={`badge bg-primary ${getConnectionColor(entry.connectionType as ConnectionType)}`}>
                                                    <FontAwesomeIcon icon={getConnectionIcon(entry.connectionType as ConnectionType)} className="me-1" />
                                                    {entry.connectionType}
                                                </span>
                                            )}
                                        </div>
                                        <div className="card-body">
                                            <div className="table-responsive">
                                                <table className="table table-hover mb-0">
                                                    <thead className="table-light">
                                                        <tr>
                                                            <th className="fw-bold">Metric</th>
                                                            <th className="fw-bold">Value</th>
                                                            <th className="fw-bold">Status</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {entry.data.map((d: any, i: number) => (
                                                            <tr key={i}>
                                                                <td className="fw-semibold">{d.name}</td>
                                                                <td className="fs-5">{d.value}</td>
                                                                <td>
                                                                    <span className="badge bg-success">Normal</span>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                ))}

                                {/* Enhanced No Readings Message */}
                                {readings.length === 0 && (
                                    <div className="alert alert-warning border-0 shadow-sm text-center py-5">
                                        <FontAwesomeIcon icon={faExclamationTriangle} className="fa-3x text-warning mb-3" />
                                        <h5 className="text-warning">No Device Readings Available</h5>
                                        <p className="text-muted mb-0">Please connect to a device to view readings and data.</p>
                                    </div>
                                )}

                                {/* Enhanced Action Buttons */}
                                {showExportOptions && readings.length > 0 && (
                                    <div className="text-center mt-5">
                                        <div className="row g-3 justify-content-center">
                                            <div className="col-auto">
                                                <button className="btn btn-success btn-lg px-4" onClick={exportPDF}>
                                                    <FontAwesomeIcon icon={faDownload} className="me-2" />
                                                    Export to PDF
                                                </button>
                                            </div>
                                            <div className="col-auto">
                                                <button className="btn btn-primary btn-lg px-4" onClick={shareToCollaboration}>
                                                    <FontAwesomeIcon icon={faUpload} className="me-2" />
                                                    Share to Collaboration
                                                </button>
                                            </div>
                                            <div className="col-auto">
                                                <button className="btn btn-warning btn-lg px-4" onClick={startNewCollaboration}>
                                                    <FontAwesomeIcon icon={faPlus} className="me-2" />
                                                    Start New Collaboration
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Enhanced Back Button */}
                                <div className="text-center mt-5">
                                    <button 
                                        className="btn btn-secondary btn-lg px-4" 
                                        onClick={() => {
                                            if (connectedDevice) {
                                                Swal.fire({
                                                    title: 'Disconnect Device?',
                                                    text: 'Going back will disconnect the current device. Continue?',
                                                    icon: 'warning',
                                                    showCancelButton: true,
                                                    confirmButtonText: 'Yes, disconnect and go back',
                                                    cancelButtonText: 'No, stay here'
                                                }).then(async (result) => {
                                                    if (result.isConfirmed) {
                                                        await handleDisconnect();
                                                        setStep(2);
                                                    }
                                                });
                                            } else {
                                                setStep(2);
                                            }
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faTimesCircle} className="me-2" />
                                        Back to Device Selection
                                    </button>
                                </div>
                            </div>
                        )}

                    </div>
                </div>
            </div>

            <Canvas></Canvas>
            <DashboardFooter />
        </>
    )
}

export default Devices;
